import {
  CopyMdIcon,
  IconButton,
  OpenCalendarMdIcon,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import { ReactComponent as GoogleCalendarIcon } from 'assets/images/google-calendar.svg';
import { ReactComponent as GoogleMeetingIcon } from 'assets/images/google-meeting.svg';
import CrmIcon from 'components/CrmIcon';
import { MeetingActivity } from 'crono-fe-common/types/event';

interface MeetingLinkRowsProps {
  meetingInfo?: MeetingActivity | null;
  isCalendarSynced: boolean;
  crmUrl?: string | null;
  isLoading?: boolean;
  onCopy: (text: string, label: string) => void;
}

const MeetingLinkRows = ({
  meetingInfo,
  isCalendarSynced,
  crmUrl,
  isLoading,
  onCopy,
}: MeetingLinkRowsProps) => {
  if (isLoading) {
    return (
      <>
        <div className="h-4 w-48 rounded bg-neutral-300 animate-pulse" />
        <div className="h-4 w-36 rounded bg-neutral-300 animate-pulse" />
      </>
    );
  }

  const conferenceLink = meetingInfo?.conferenceLink;

  return (
    <>
      {(isCalendarSynced || conferenceLink) && (
        <div className="flex items-center gap-2">
          <GoogleMeetingIcon className="size-4" />

          <Tooltip>
            <TooltipTrigger asChild>
              <span className="max-w-50 xl:max-w-80 text-neutral-800 text-sm truncate">
                {conferenceLink || '-'}
              </span>
            </TooltipTrigger>

            {conferenceLink && (
              <TooltipContent className="z-99999">
                {conferenceLink}
              </TooltipContent>
            )}
          </Tooltip>

          {conferenceLink && (
            <IconButton
              label="Copy meeting link to clipboard"
              variant="text"
              icon={CopyMdIcon}
              onClick={() => onCopy(conferenceLink, 'Meeting link')}
            />
          )}
        </div>
      )}

      {isCalendarSynced && (
        <div className="flex items-center gap-2">
          <GoogleCalendarIcon className="size-4" />

          <Tooltip>
            <TooltipTrigger asChild>
              <span className="max-w-50 xl:max-w-80 text-neutral-800 text-sm truncate">
                {meetingInfo?.organizerEmail || '-'}
              </span>
            </TooltipTrigger>

            {meetingInfo?.organizerEmail && (
              <TooltipContent className="z-99999">
                {meetingInfo.organizerEmail}
              </TooltipContent>
            )}
          </Tooltip>

          {meetingInfo?.organizerEmail && meetingInfo?.calendarId && (
            <Separator
              orientation="vertical"
              className="h-5 text-neutral-300"
            />
          )}

          {meetingInfo?.calendarId && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="max-w-50 xl:max-w-80 text-neutral-600 text-xs truncate capitalize">
                  {meetingInfo.calendarId}
                </span>
              </TooltipTrigger>

              <TooltipContent className="z-99999">
                {meetingInfo.calendarId}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}

      {crmUrl && (
        <div className="flex items-center gap-2">
          <CrmIcon className="size-4" />

          <Tooltip>
            <TooltipTrigger asChild>
              <span className="max-w-50 xl:max-w-80 text-neutral-800 text-sm truncate">
                {crmUrl}
              </span>
            </TooltipTrigger>

            <TooltipContent className="z-99999">
              <span className="wrap-break-word">{crmUrl}</span>
            </TooltipContent>
          </Tooltip>

          <IconButton
            label="Open meeting in CRM"
            variant="text"
            icon={OpenCalendarMdIcon}
            onClick={() => window.open(crmUrl, '_blank')}
          />
        </div>
      )}
    </>
  );
};

export default MeetingLinkRows;
